import { Card, CardAction, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { DataProvider, useAsyncData } from '@repo/ui/dataProvider'
import { getLocale, useT } from '@repo/ui/hooks'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { Separator } from '@repo/ui/separator'
import { sortByKey } from '@repo/ui/utils'
import { format } from 'date-fns'
import { MessageSquareText } from 'lucide-react'

import { CurrentChatSession } from '@/components/features/chat-sessions/CurrentChatSession'
import { QrCodeViewer } from '@/components/features/chat-sessions/QrCodeViewer'
import { SkeletonNote } from '@/components/skeletons/SkeletonNote'
import { useView } from '@/hooks'
import { RootState } from '@/store'
import { Beneficiary } from '@/store/beneficiary'
import { ChatSession } from '@/store/chat'
import { getCurrentBeneficiary, getCurrentOrganization } from '@/store/helpers'
import { Organization } from '@/store/user'

export function ChatSessions() {
  return (
    <DataProvider
      loadingComponent={<SkeletonNote />}
      selectors={[
        (state: RootState) => state.chat.sessions,
        getCurrentBeneficiary,
        getCurrentOrganization,
      ]}
      render={(chatSessions, beneficiary, organization) => (
        <Card className="flex w-full flex-col">
          <CardHeader className="mb-4">
            <Header
              beneficiary={beneficiary}
              chatSessions={chatSessions}
              organization={organization}
            />
          </CardHeader>
          {chatSessions.length > 0 && <Separator />}
          <div className="px-4">
            <CurrentChatSession autoScroll={false} />
          </div>
        </Card>
      )}
    />
  )
}

function Header({
  beneficiary,
  chatSessions,
  organization,
}: {
  beneficiary: Beneficiary
  chatSessions: ChatSession[]
  organization: Organization
}) {
  const { t } = useT({ keyPrefix: 'chat.modal' })
  const view = useView()

  const {
    data: [currentSession],
  } = useAsyncData([(state) => state.chat.currentSession])

  return (
    <>
      {chatSessions.length > 0 ? (
        <>
          <CardTitle>{t('title', { fullName: beneficiary.fullName })}</CardTitle>
          <CardDescription className="pb-4">{t('description')}</CardDescription>
          <div className="flex items-center justify-between gap-4">
            <Select
              key={currentSession?.id || 'select'}
              onValueChange={(id) => view.beneficiary().chat(id).navigate()}
              defaultValue={currentSession?.id}
              disabled={!currentSession}
            >
              <SelectTrigger className="max-w-96">
                <SelectValue placeholder={t('selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {[...chatSessions].sort(sortByKey('startedAt', 'desc')).map((v) => {
                  const startDate = new Date(v.startedAt)
                  return (
                    <SelectItem value={v.id} key={v.id}>
                      <div className="flex items-center">
                        <MessageSquareText className="mr-2 size-4" />
                        {format(startDate, 'PPPP', { locale: getLocale() })}
                      </div>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            <QrCodeViewer
              key={beneficiary.id}
              beneficiary={beneficiary}
              chatSessions={chatSessions}
              organization={organization}
            />
          </div>
        </>
      ) : (
        <CardAction className="justify-center">
          <QrCodeViewer
            key={beneficiary.id}
            beneficiary={beneficiary}
            chatSessions={chatSessions}
            organization={organization}
            variant="default"
          />
        </CardAction>
      )}
    </>
  )
}
